<template lang="pug">
div.carousel-card(
  :class="{ selected }"
  @click="$emit('select')"
  @mouseenter="hovered = true"
  @mouseleave="hovered = false"
)
  .card-hover-actions(v-show="hovered")
    v-btn(icon small title="Copy row" @click.stop="copyRow" :loading="copying")
      v-icon(small) content_copy
    v-btn(icon small title="Export card" @click.stop="exportCard" :loading="exporting")
      v-icon(small) download
    v-btn(icon small title="Delete row" @click.stop="confirmDelete = true")
      v-icon(small color="red lighten-2") delete

  .card-preview
    template-renderer(:game="game" :component="component" :item="item" :compact="true" :render-priority="itemIndex" :auto-refresh-layers="false" :paused="paused" @rendered="$emit('rendered')")

  .card-index.caption.text-center.mt-1 {{ itemIndex + 1 }}

  v-dialog(v-model="confirmDelete" max-width="360")
    v-card
      v-card-title.subtitle-1
        | Delete row {{ itemIndex + 1 }}?
      v-card-text
        | This will permanently delete this entire row from the spreadsheet.
        br
        strong This cannot be undone.
      v-card-actions
        v-spacer
        v-btn(text small @click="confirmDelete = false") Cancel
        v-btn(text small color="error" :loading="deleting" @click="deleteRow") Delete
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import TemplateRenderer from '../template/TemplateRenderer.vue'
import pdfRenderer from '../../services/pdf_renderer'

export default {
  props: ['game', 'component', 'item', 'itemIndex', 'selected', 'paused'],

  components: { TemplateRenderer },

  data() {
    return {
      hovered: false,
      copying: false,
      exporting: false,
      deleting: false,
      confirmDelete: false,
    }
  },

  computed: {
    ...mapGetters(['findSpreadsheetWorksheet', 'findComponentTemplate']),

    worksheetTitle() {
      const ws = this.findSpreadsheetWorksheet(this.component.spreadsheetId, this.component.worksheetId)
      return ws ? ws.title : ''
    },

    rowValues() {
      return this.item.map(field => field.value)
    },
  },

  methods: {
    ...mapActions(['googleInsertSheetRow', 'googleUpdateSheetRow', 'googleDeleteSheetRow', 'refreshSheetIndex']),

    async copyRow() {
      this.copying = true
      try {
        // Insert a blank row immediately after this one (0-indexed: header=0, data starts at 1)
        await this.googleInsertSheetRow({
          spreadsheetId: this.component.spreadsheetId,
          sheetId: this.component.worksheetId,
          rowIndex: this.itemIndex + 2
        })
        // Fill the new row with this row's values (1-indexed row number = itemIndex + 3)
        await this.googleUpdateSheetRow({
          spreadsheetId: this.component.spreadsheetId,
          worksheetTitle: this.worksheetTitle,
          rowNumber: this.itemIndex + 3,
          values: this.rowValues
        })
        await this.refreshSheetIndex(this.component.spreadsheetId)
      } finally {
        this.copying = false
      }
    },

    async exportCard() {
      this.exporting = true
      try {
        const template = this.findComponentTemplate(this.component)
        const blob = await pdfRenderer.renderItemToPdf(this.game, this.component, this.item, template)
        const a = document.createElement('a')
        a.href = blob
        a.download = `card-${this.itemIndex + 1}.pdf`
        a.click()
      } finally {
        this.exporting = false
      }
    },

    async deleteRow() {
      this.deleting = true
      try {
        await this.googleDeleteSheetRow({
          spreadsheetId: this.component.spreadsheetId,
          sheetId: this.component.worksheetId,
          rowIndex: this.itemIndex + 1
        })
        await this.refreshSheetIndex(this.component.spreadsheetId)
        this.confirmDelete = false
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>

<style scoped>
.carousel-card {
  position: relative;
  flex-shrink: 0;
  width: 200px;
  margin-right: 12px;
  cursor: pointer;
  border-radius: 4px;
  transition: box-shadow 0.15s ease;
}
.carousel-card.selected {
  box-shadow: 0 0 0 2px #1976d2;
}
.theme--dark .carousel-card.selected {
  box-shadow: 0 0 0 2px #64b5f6;
}
.card-preview {
  border-radius: 3px;
  overflow: hidden;
}
.card-hover-actions {
  position: absolute;
  top: -32px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 4px;
  z-index: 2;
  background: rgba(0,0,0,0.55);
  border-radius: 4px 4px 0 0;
  padding: 2px 4px;
}
</style>
